import { Button, Modal, Select, Stack, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { enumOptions } from '../i18n';

type Props = {
    opened: boolean;
    onClose: () => void;
    onCreated: (cityId: number) => void;
};

export const CityCreateDialog = ({ opened, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const countryOptions = useMemo(
        () => enumOptions(Object.values(CountriesEnum), t, 'enums.countries'),
        [t],
    );
    const [title, setTitle] = useState('');
    const [country, setCountry] = useState<CountriesEnum | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!opened) return;
        setTitle('');
        setCountry(null);
    }, [opened]);

    const create = async () => {
        if (!title.trim() || !country) {
            notifications.show({
                color: 'red',
                message: t('editors.cityCreate.titleCountryRequired'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.cities.create({
                data: { title: title.trim(), country },
            });

            if (data?.id) {
                notifications.show({
                    color: 'green',
                    message: t('editors.cityCreate.created', {
                        title: data.title,
                        id: data.id,
                    }),
                });
                onCreated(data.id);
                onClose();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={t('editors.cityCreate.title')} centered>
            <Stack gap='md'>
                <TextInput
                    label={t('editors.cityCreate.titleField')}
                    required
                    placeholder={t('editors.cityCreate.titlePlaceholder')}
                    value={title}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                />
                <Select
                    label={t('editors.cityCreate.country')}
                    required
                    searchable
                    data={countryOptions}
                    value={country}
                    onChange={(v) => setCountry((v as CountriesEnum) || null)}
                />
                <Button loading={saving} onClick={create}>
                    {t('editors.cityCreate.create')}
                </Button>
            </Stack>
        </Modal>
    );
};
